import { CommonModule } from '@angular/common';
import { Component, HostListener, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Subscription } from 'rxjs';
import {
  AsignacionesService,
  EmployeeNotification,
  EmployeeRef,
} from 'src/app/services/asignaciones.service';
import { TicketsWebSocketService } from 'src/app/services/tickets-websocket.service';
import { getAsignacionesActorEmployeeId } from '../../utils/asignaciones-actor.util';

@Component({
  selector: 'app-asignaciones-notifications',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule],
  templateUrl: './asignaciones-notifications.component.html',
  styleUrls: ['./asignaciones-notifications.component.scss'],
})
export class AsignacionesNotificationsComponent implements OnInit, OnDestroy {
  @Input() employees: EmployeeRef[] = [];

  open = signal(false);
  loading = signal(false);
  unreadCount = signal(0);
  notifications = signal<EmployeeNotification[]>([]);

  private employeeId: number | undefined = getAsignacionesActorEmployeeId();
  private refreshTimer: ReturnType<typeof setInterval> | null = null;
  private wsSubs = new Subscription();

  constructor(
    private service: AsignacionesService,
    private ticketsWs: TicketsWebSocketService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.resolveEmployeeId();
    this.refreshUnreadCount();
    this.refreshTimer = setInterval(() => this.refreshUnreadCount(), 60_000);
    this.connectRealtime();
  }

  ngOnDestroy(): void {
    if (this.refreshTimer) clearInterval(this.refreshTimer);
    this.wsSubs.unsubscribe();
    this.ticketsWs.disconnect();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target?.closest('.asignaciones-notifications')) {
      this.open.set(false);
    }
  }

  toggle(event: MouseEvent): void {
    event.stopPropagation();
    const next = !this.open();
    this.open.set(next);
    if (next) {
      this.loadNotifications();
    }
  }

  markAllRead(event: MouseEvent): void {
    event.stopPropagation();
    if (!this.employeeId) return;
    this.service.markAllNotificationsRead(this.employeeId).subscribe(() => {
      this.unreadCount.set(0);
      this.notifications.update((list) => list.map((n) => ({ ...n, readAt: n.readAt || new Date().toISOString() })));
    });
  }

  openNotification(notification: EmployeeNotification, event: MouseEvent): void {
    event.stopPropagation();
    if (!this.employeeId) return;

    if (!notification.readAt) {
      this.service.markNotificationRead(notification.id, this.employeeId).subscribe(() => {
        this.unreadCount.update((c) => Math.max(0, c - 1));
        this.notifications.update((list) =>
          list.map((n) =>
            n.id === notification.id ? { ...n, readAt: new Date().toISOString() } : n,
          ),
        );
      });
    }

    this.open.set(false);
    this.router.navigate(['/asignaciones/ticket', notification.ticketId]);
  }

  private resolveEmployeeId(): void {
    if (this.employeeId) return;
    this.employeeId = getAsignacionesActorEmployeeId(this.employees);
  }

  private connectRealtime(): void {
    this.resolveEmployeeId();
    this.ticketsWs.connect(this.employeeId);
    if (this.employeeId) {
      this.ticketsWs.joinEmployee(this.employeeId);
    }
    this.wsSubs.add(
      this.ticketsWs.onNotificationCreated().subscribe((event) => {
        if (!event?.notification) return;
        if (this.employeeId && event.employeeId !== this.employeeId) return;
        const notification = event.notification;
        this.notifications.update((list) => {
          if (list.some((n) => n.id === notification.id)) return list;
          return [notification, ...list].slice(0, 30);
        });
        if (!notification.readAt) {
          this.unreadCount.update((c) => c + 1);
        }
      }),
    );
  }

  private refreshUnreadCount(): void {
    this.resolveEmployeeId();
    if (!this.employeeId) return;
    this.service.getNotificationUnreadCount(this.employeeId).subscribe((res) => {
      if (res?.ok && res.data) {
        this.unreadCount.set(res.data.count);
      }
    });
  }

  private loadNotifications(): void {
    this.resolveEmployeeId();
    if (!this.employeeId) return;
    this.loading.set(true);
    this.service.getNotifications(this.employeeId, 30, 1).subscribe({
      next: (res) => {
        this.notifications.set(res?.data?.notifications || []);
        this.loading.set(false);
        this.refreshUnreadCount();
      },
      error: () => this.loading.set(false),
    });
  }
}
